import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Users,
  Clock,
  BookOpen,
  ArrowRight,
  RefreshCw,
  MessageSquare,
  AlertCircle,
  AlertTriangle,
  GraduationCap,
  Play,
  Flame,
  Star
} from 'lucide-react'
import { OnboardingProgressWidget } from '@/components/onboarding'
import { MatrixRain } from '@/components/ui/MatrixRain'
import { apiClient } from '@/services/api'
import { useUserRole } from '@/store/authStore'
import { useTerminology } from '@/hooks/useTerminology'
import type { DashboardData, Course, CaptainLeaderboardEntry } from '@/types'
import { DEFAULT_ORG_ID } from '@/constants'
import styles from './Dashboard.module.css'

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const LEADERBOARD_TABS = [
  { key: 'most_courses_run' as const, label: 'Runs', icon: Play, suffix: '' },
  { key: 'longest_streaks' as const, label: 'Streaks', icon: Flame, suffix: 'd' },
  { key: 'most_students_hosted' as const, label: 'Students', icon: Users, suffix: '' },
] as const

export function Dashboard() {
  const navigate = useNavigate()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const role = useUserRole()
  const t = useTerminology()
  const [error, setError] = useState<string | null>(null)
  const [courses, setCourses] = useState<Course[]>([])
  const [coursesLoading, setCoursesLoading] = useState(false)
  const [enrollingId, setEnrollingId] = useState<string | null>(null)
  const [showCreditsModal, setShowCreditsModal] = useState(false)
  const [leaderboardTab, setLeaderboardTab] = useState<typeof LEADERBOARD_TABS[number]['key']>('most_courses_run')

  const fetchDashboard = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.getDashboard()
      setData(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (role !== 'pirate') {
      fetchDashboard()
    } else {
      setLoading(false)
    }
  }, [role])

  useEffect(() => {
    if (role === 'pirate') {
      setCoursesLoading(true)
      apiClient.getCourses(DEFAULT_ORG_ID)
        .then(res => setCourses(res.data))
        .catch(() => {})
        .finally(() => setCoursesLoading(false))
    }
  }, [role])

  const handleEnroll = async (course: Course) => {
    setEnrollingId(course.id)
    try {
      await apiClient.enrollInCourseSelfPaced(course.course_code)
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, enrolled: true, enrolled_count: c.enrolled_count + 1 } : c))
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 402) {
        setShowCreditsModal(true)
      } else {
        const msg = err?.response?.data?.error || 'Failed to enroll'
        alert(msg)
      }
    } finally {
      setEnrollingId(null)
    }
  }

  // Captain's courses (for stats count)
  const [captainCourses, setCaptainCourses] = useState<Course[]>([])

  useEffect(() => {
    if (role === 'captain') {
      apiClient.getCourses(DEFAULT_ORG_ID)
        .then(res => setCaptainCourses(res.data || []))
        .catch(() => {})
    }
  }, [role])

  const myCourseCount = captainCourses.filter(c => c.source_course_id != null).length

  const stats = data ? [
    {
      name: role === 'captain' ? `Total ${t.course.titlePlural}` : `Total ${t.lesson.titlePlural}`,
      value: role === 'captain' ? String(myCourseCount) : String(data.stats.total_lessons),
      icon: BookOpen,
    },
    {
      name: 'Total Students',
      value: String(data.stats.total_students),
      icon: Users,
    },
    {
      name: 'Total Responses',
      value: String(data.stats.total_responses),
      icon: MessageSquare,
    },
    {
      name: 'Avg Duration',
      value: `${data.stats.avg_lesson_duration}m`,
      icon: Clock,
    },
  ] : []

  const formatTime = (isoString: string | null) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHrs = Math.floor(diffMin / 60)
    if (diffHrs < 24) return `${diffHrs}h ago`
    return date.toLocaleDateString()
  }

  const featuredCourses = data?.featured_courses ?? []
  const activeTab = LEADERBOARD_TABS.find(t => t.key === leaderboardTab)!
  const leaderboardEntries = data?.captain_leaderboards?.[leaderboardTab] ?? []

  return (
    <div className={styles.dashboard}>
      <MatrixRain />
      <div className={styles.container}>
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={styles.welcomeHeader}
        >
          <div className={styles.welcomeContent}>
            <div>
              <h1 className={styles.welcomeTitle}>
                {role === 'admin'
                  ? `${getGreeting()}, Admin!`
                  : role === 'captain'
                  ? `${getGreeting()}, Captain!`
                  : `${getGreeting()}, Pirate!`}
              </h1>
              <p className={styles.welcomeSubtitle}>
                {role === 'admin'
                  ? `Manage ${t.course.lowerPlural}, users, and platform settings`
                  : role === 'captain'
                  ? `View your ${t.course.lowerPlural} and lessons`
                  : `Explore your enrolled ${t.course.lowerPlural} and start learning`}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={styles.startLessonBtn}
              onClick={() => navigate(role === 'admin' ? '/admin/users' : '/my-courses')}
            >
              {role === 'admin' ? 'MANAGE USERS' : `MY ${t.course.upperPlural}`}
            </motion.button>
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles.loadingState}
          >
            <RefreshCw className={`w-8 h-8 ${styles.loadingIcon}`} />
            <p className={styles.loadingText}>Loading dashboard...</p>
          </motion.div>
        )}

        {/* Error State */}
        {error && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles.loadingState}
          >
            <AlertCircle className={`w-8 h-8 ${styles.errorIcon}`} />
            <p className={styles.errorText}>{error}</p>
            <button onClick={fetchDashboard} className={styles.retryBtn}>
              RETRY
            </button>
          </motion.div>
        )}

        {/* Pirate View — available courses */}
        {role === 'pirate' && !coursesLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className={`${styles.sectionTitle} mb-5`}>{`AVAILABLE ${t.course.upperPlural}`}</h3>
            {courses.length === 0 ? (
              <div className={`${styles.recentLessons} ${styles.pirateEmptyContainer}`}>
                <GraduationCap className={`w-12 h-12 ${styles.emptyIcon}`} />
                <p className={styles.pirateEmptyTitle}>
                  {`No ${t.course.lowerPlural} available yet.`}
                </p>
                <p className={styles.emptySubtext}>
                  Check back later or ask your captain!
                </p>
              </div>
            ) : (
              <div className={styles.courseGrid}>
                {courses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className={styles.courseCard}
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => navigate(`/organizations/${DEFAULT_ORG_ID}/courses/${course.id}`)}
                    >
                      {course.image_url ? (
                        <img src={course.image_url} alt={course.name} className={styles.courseCardImage} />
                      ) : (
                        <div className={styles.courseCardImagePlaceholder}>
                          <BookOpen className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div className={styles.courseCardBody}>
                      <h4
                        className={`${styles.courseCardTitle} cursor-pointer`}
                        onClick={() => navigate(`/organizations/${DEFAULT_ORG_ID}/courses/${course.id}`)}
                      >
                        {course.name}
                      </h4>
                      <p className={styles.courseCardDesc}>{course.description || 'No description'}</p>
                      <div className={styles.courseCardMeta}>
                        <span><BookOpen className={`w-4 h-4 ${styles.metaIcon}`} />{course.lesson_count} {t.lesson.lowerPlural}</span>
                        <span><Users className={`w-4 h-4 ${styles.metaIcon}`} />{course.instructor_count ?? 0} captains aboard</span>
                      </div>
                      <div className={styles.courseBadges}>
                        {course.estimated_duration_hours ? (
                          <span className={styles.durationBadge}>
                            <Clock className="w-3.5 h-3.5" />~{course.estimated_duration_hours}h
                          </span>
                        ) : null}
                        <span className={styles.costBadge} data-free={String((course.self_paced_credit_cost ?? 0) === 0)}>
                          {(course.self_paced_credit_cost ?? 0) > 0 ? `${course.self_paced_credit_cost} credits` : 'FREE'}
                        </span>
                      </div>
                      {course.enrolled ? (
                        <button
                          className={styles.courseCardBtn}
                          onClick={() => navigate(`/course/${course.course_code}`)}
                        >
                          {`CONTINUE ${t.course.upper}`}
                        </button>
                      ) : (
                        <button
                          className={styles.courseCardBtn}
                          disabled={enrollingId === course.id}
                          onClick={() => handleEnroll(course)}
                        >
                          {enrollingId === course.id ? 'ENROLLING...' : 'ENROLL'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {role === 'pirate' && coursesLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.loadingState}>
            <RefreshCw className={`w-8 h-8 ${styles.loadingIcon}`} />
            <p className={styles.loadingText}>Loading courses...</p>
          </motion.div>
        )}

        {/* Captain / Admin View — stats + recent lessons */}
        {role !== 'pirate' && !loading && !error && data && (
          <>
            {/* Featured Courses */}
            {featuredCourses.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={styles.featuredSection}
              >
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>FEATURED COURSES</h3>
                  <button
                    className={styles.viewAllBtn}
                    onClick={() => navigate('/courses')}
                  >
                    View all
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
                <div className={styles.featuredGrid}>
                  {featuredCourses.map((course, index) => (
                    <motion.div
                      key={course.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + index * 0.05 }}
                      className={styles.featuredCard}
                      onClick={() => navigate(`/organizations/${DEFAULT_ORG_ID}/courses/${course.id}`)}
                    >
                      {course.image_url ? (
                        <img src={course.image_url} alt={course.name} className={styles.featuredCardImage} />
                      ) : (
                        <div className={styles.featuredCardImagePlaceholder}>
                          <BookOpen className="w-10 h-10" />
                        </div>
                      )}
                      <div className={styles.featuredCardOverlay}>
                        <span className={styles.featuredBadge}>
                          <Star className="w-3.5 h-3.5" />
                          Featured
                        </span>
                        <h4 className={styles.featuredCardTitle}>{course.name}</h4>
                        {course.description && (
                          <p className={styles.featuredCardDesc}>{course.description}</p>
                        )}
                        <div className={styles.featuredCardMeta}>
                          <span>{course.lesson_count} {t.lesson.lowerPlural}</span>
                          <span>{course.instructor_count ?? 0} captains aboard</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className={styles.statsGrid}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + index * 0.05 }}
                  className={styles.statCard}
                >
                  <div className={styles.statContent}>
                    <div className={styles.statInfo}>
                      <p className={styles.statName}>{stat.name}</p>
                      <p className={styles.statValue}>{stat.value}</p>
                    </div>
                    <div className={styles.statIcon}>
                      <stat.icon />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <div className={styles.contentGrid}>
              {/* Recent Lessons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={styles.recentLessons}
              >
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>{`RECENT ${t.lesson.upperPlural}`}</h3>
                  <button
                    className={styles.viewAllBtn}
                    onClick={() => navigate('/my-courses')}
                  >
                    View all
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>

                {data.recent_lessons.length === 0 ? (
                  <div className={styles.emptyState}>
                    <BookOpen className={`w-10 h-10 ${styles.emptyIcon}`} />
                    <p className={styles.emptyText}>
                      {role === 'captain'
                        ? `No ${t.lesson.lowerPlural} run yet. Go to your ${t.course.lowerPlural} to get started.`
                        : `No ${t.lesson.lowerPlural} yet. Create your first ${t.course.lower} to get started!`}
                    </p>
                    <button onClick={() => navigate(role === 'admin' ? '/courses' : '/my-courses')} className={styles.emptyAction}>
                      {role === 'admin' ? `CREATE ${t.course.upper}` : `MY ${t.course.upperPlural}`}
                    </button>
                  </div>
                ) : (
                  <div className={styles.lessonsList}>
                    {data.recent_lessons.map((lesson, index) => (
                      <motion.div
                        key={lesson.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className={`${styles.lessonItem} cursor-pointer`}
                        onClick={() => {
                          if (lesson.course_id && lesson.organization_id) {
                            navigate(`/organizations/${lesson.organization_id}/courses/${lesson.course_id}`, { state: { from: 'my-courses' } })
                          }
                        }}
                      >
                        <div className={styles.lessonInfo}>
                          <div className={`${styles.lessonStatus} ${styles[lesson.status] || ''}`}></div>
                          <div className={styles.lessonDetails}>
                            <h4>{lesson.name}</h4>
                            <p>
                              Code: {lesson.run_code} &bull; {lesson.player_count} students &bull; {lesson.activity_count} activities
                            </p>
                          </div>
                        </div>
                        <div className={styles.lessonMeta}>
                          <span className={`${styles.lessonStatusBadge} ${styles[lesson.status] || ''}`}>
                            {lesson.status}
                          </span>
                          <p className={styles.lessonTime}>
                            {lesson.started_at ? formatTime(lesson.started_at) : formatTime(lesson.created_at)}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Side Panel */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={styles.sidePanel}
              >
                {/* Onboarding Missions */}
                {role === 'captain' && <OnboardingProgressWidget />}

                {/* Compact Tabbed Leaderboard */}
                {data.captain_leaderboards && (
                  <div className={styles.compactLeaderboard}>
                    <h3 className={styles.sectionTitle}>LEADERBOARD</h3>
                    <div className={styles.leaderboardTabs}>
                      {LEADERBOARD_TABS.map(({ key, label, icon: Icon }) => (
                        <button
                          key={key}
                          className={`${styles.leaderboardTabBtn} ${leaderboardTab === key ? styles.leaderboardTabActive : ''}`}
                          onClick={() => setLeaderboardTab(key)}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      ))}
                    </div>
                    {leaderboardEntries.length === 0 ? (
                      <p className={styles.leaderboardEmpty}>No data yet</p>
                    ) : (
                      <div className={styles.leaderboardList}>
                        {leaderboardEntries.slice(0, 5).map((entry: CaptainLeaderboardEntry, i: number) => (
                          <div key={i} className={styles.leaderboardRow}>
                            <span className={styles.leaderboardRank} data-rank={String(i + 1)}>{i + 1}</span>
                            <span className={styles.leaderboardName}>{entry.display_name}</span>
                            <span className={styles.leaderboardValue}>{entry.value}{activeTab.suffix}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      className={styles.viewAllBtn}
                      onClick={() => navigate('/leaderboard')}
                      style={{ marginTop: 12 }}
                    >
                      Full board
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                )}

              </motion.div>
            </div>
          </>
        )}
      </div>

      {/* Insufficient Credits Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--color-dropdown-bg,var(--color-surface))] rounded-xl p-6 max-w-md w-full border border-[var(--color-border)]"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--color-text)]">Insufficient Credits</h3>
            </div>
            <p className="text-[var(--color-text)] text-sm mb-6">
              You don't have enough credits to enrol in this {t.course.lower}. Purchase more credits to continue.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCreditsModal(false)}
                className="px-4 py-2 bg-[var(--color-input-bg)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/users/plans')}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                Buy Credits
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
